---
title: Privacy
updated: 2026-10-02
---

## 1. Verwerkingsverantwoordelijke

Draughts Game ("wij", "ons") is de verwerkingsverantwoordelijke voor de persoonsgegevens die via dit platform worden verzameld. Contact: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)

## 2. Gegevens die We Verzamelen

### 2.1 Accountgegevens

- **Gebruikersnaam** - door jou gekozen, openbaar zichtbaar
- **E-mailadres** - in rust versleuteld met AES-256-GCM, gebruikt voor accountidentificatie, niet openbaar zichtbaar
- **Wachtwoord** - opgeslagen als bcrypt-hash (kostenfactor 12), nooit in platte tekst
- **Leeftijdsbevestiging** - we registreren dat je bij registratie hebt bevestigd minstens 16 jaar oud te zijn
- **E-mailverificatiestatus** - of je je e-mailadres hebt bevestigd, plus kortlevende verificatie- en wachtwoordresettokens
- **Profielpersonalisatie (optioneel)** - avatar en land, door jou gekozen, openbaar zichtbaar

### 2.2 Spelgegevens

- Zetten, resultaten en tijdstempels van partijen
- Spelersratings en statistieken
- Toernooideelname en resultaten
- Puzzelpogingen, puzzelrating en oplosreeksen (streaks)
- Je studiebibliotheek: stellingnotities (vrije tekst die je zelf schrijft), mappen, bewaarde partijen en in de editor bewaarde stellingen — privé, alleen voor jou zichtbaar

### 2.3 Technische Gegevens

- IP-adres (voor beveiliging en anti-cheating)
- User-agentstring van de browser
- Sessietokens

### 2.4 Supportverzoeken

Als je contact met ons opneemt via het supportformulier, verzamelen we je e-mailadres, een optionele naam, en het onderwerp en de inhoud van je bericht, zodat we je verzoek kunnen afhandelen en beantwoorden.

## 3. Rechtsgrondslag voor de Verwerking

We verwerken je persoonsgegevens op basis van:

- **Uitvoering van de overeenkomst** (art. 6(1)(b) AVG) - om de speldienst te leveren waarvoor je je hebt aangemeld, inclusief transactionele e-mails (accountverificatie, wachtwoordreset) en supportantwoorden.
- **Gerechtvaardigde belangen** (art. 6(1)(f) AVG) - voor platformbeveiliging, anti-cheating, geaggregeerde spelstatistieken (welke varianten worden gespeeld, hoe partijen eindigen) en verbetering van de dienst.
- **Toestemming** (art. 6(1)(a) AVG) - voor de gebruiksmeting via Umami en de foutregistratie via Sentry. Geen van beide start voordat je in de banner accepteert.

## 4. Hoe We Je Gegevens Gebruiken

- Het leveren en beheren van het spelplatform
- Je account authenticeren en sessies onderhouden
- Het berekenen en weergeven van spelersratings en ranglijsten
- Matchmaking en toernooiorganisatie
- Het opsporen en voorkomen van valsspelen en misbruik
- Het versturen van transactionele e-mails (accountverificatie, wachtwoordreset)
- Het afhandelen en beantwoorden van je supportverzoeken
- Het verbeteren van de dienst via geaggregeerde, anonieme statistieken
- Het analyseren van spelgegevens (bijv. gespeelde varianten, resultaten) in geaggregeerde, geanonimiseerde vorm om het gebruik te begrijpen en het platform te verbeteren

## 5. Cookies en Diensten van Derden

We gebruiken essentiële cookies voor authenticatie en je voorkeuren. We maken gebruik van de volgende diensten:

- **Umami** (statistieken) - self-hosted op onze eigen EU-infrastructuur. Het is cookievrij en start alleen als je statistieken accepteert in de banner. Bezoeken worden herkend aan een pseudonieme vingerafdruk, afgeleid van je IP-adres en browser en dagelijks vernieuwd; de cijfers worden uitsluitend geaggregeerd gebruikt en we volgen je niet op andere sites.
- **Sentry** (foutregistratie) - alleen ingeschakeld met jouw toestemming.
- **Resend** (e-mailbezorging) - bezorgt onze transactionele e-mails (accountverificatie, wachtwoordreset) en supportmeldingen.
- **DigitalOcean** (hosting) - EU-datacenter (Frankfurt).

We verkopen je gegevens niet en gebruiken geen advertentietrackers.

## 6. Bewaartermijnen

- **Accountgegevens** - bewaard zolang je account actief is.
- **Gastaccounts** - automatisch verwijderd na 30 dagen inactiviteit.
- **Niet-geverifieerde accounts** - als je je registreert maar je e-mailadres nooit bevestigt, wordt het account na 30 dagen inactiviteit automatisch verwijderd.
- **Refresh-tokens** - verlopen na 7 dagen (90 dagen bij gastaccounts, zodat je je partijen zonder wachtwoord terugvindt), elk uur automatisch opgeschoond. Het IP-adres en de browsergegevens die bij een sessie horen, worden na 7 dagen gewist, ook als de sessie langer duurt.
- **E-mailverificatietokens** - verlopen na 24 uur.
- **Wachtwoordresettokens** - verlopen na 1 uur.
- **Supportverzoeken** - bewaard zolang nodig om je verzoek af te handelen.
- **Misbruikmeldingen** - wanneer een speler iemand meldt voor misbruik in de chat, wordt de chat van die partij (tot 100 berichten, van beide spelers) bewaard voor moderatie en na 90 dagen automatisch verwijderd. Verder wordt chat nooit bewaard.
- **Spelgegevens** - voor onbepaalde tijd bewaard als onderdeel van het openbare partijarchief.
- **Studiebibliotheek** - stellingnotities, mappen, bewaarde partijen en bewaarde stellingen blijven bewaard tot je ze verwijdert, en worden in elk geval verwijderd als je je account wist.
- **Sentry-foutgegevens** - automatisch verwijderd na 90 dagen.
- **Verwijderde accounts** - persoonsgegevens worden direct verwijderd bij het wissen. Partijverslagen worden geanonimiseerd (spelersverwijzing verwijderd).

## 7. Je Rechten (AVG)

Onder de Algemene Verordening Gegevensbescherming (AVG) heb je recht op:

- **Inzage** (art. 15) - download een kopie van je persoonsgegevens via je profielinstellingen ("Mijn gegevens exporteren").
- **Rectificatie** (art. 16) - wijzig je wachtwoord in je profielinstellingen. Neem voor andere correcties contact met ons op.
- **Wissing** (art. 17) - verwijder je account via je profielinstellingen ("Account verwijderen"). Dit is onmiddellijk en onomkeerbaar.
- **Overdraagbaarheid** (art. 20) - exporteer je gegevens in machineleesbaar JSON-formaat via je profielinstellingen.
- **Beperking** (art. 18) - vraag beperking van de verwerking aan door contact met ons op te nemen.
- **Bezwaar** (art. 21) - als je statistieken weigert in de cookiebanner, worden **zowel** de Umami-gebruiksmeting **als** de Sentry-foutregistratie uitgeschakeld. Je kunt je altijd bedenken via de schakelaar in je profielinstellingen.

Om deze rechten uit te oefenen, gebruik je de selfservicetools in je profielinstellingen of neem je contact met ons op via [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com). We reageren binnen 30 dagen.

## 8. Gegevensbeveiliging

We nemen passende technische en organisatorische maatregelen om je persoonsgegevens te beschermen, waaronder versleuteling van gevoelige gegevens in rust en onderweg, veilige wachtwoordhashing en rate limiting op authenticatie-endpoints.

## 9. Internationale Doorgiften

Je gegevens worden voornamelijk opgeslagen en verwerkt binnen de Europese Economische Ruimte (EER), in het datacenter van DigitalOcean in Frankfurt. Sentry-foutgegevens kunnen worden doorgegeven aan de VS onder het adequaatheidsbesluit van het EU-VS Data Privacy Framework (juli 2023). Je kunt Sentry uitschakelen in je profielinstellingen. Transactionele e-mails worden bezorgd via Resend (VS); deze doorgifte wordt gedekt door een verwerkersovereenkomst met standaardcontractbepalingen.

## 10. Privacy van Kinderen

De Dienst is niet bedoeld voor kinderen jonger dan 16 jaar (art. 8 AVG). We vragen om leeftijdsbevestiging bij registratie. We verzamelen niet bewust persoonsgegevens van kinderen jonger dan 16. Als we ontdekken dat we gegevens van een kind jonger dan 16 hebben verzameld, verwijderen we die onmiddellijk.

## 11. Wijzigingen in dit Beleid

We kunnen dit Privacybeleid van tijd tot tijd bijwerken. We stellen geregistreerde gebruikers op de hoogte van wezenlijke wijzigingen via een melding op het platform. De datum "Laatst bijgewerkt" bovenaan geeft de meest recente herziening aan.

## 12. Toezichthoudende Autoriteit

Je hebt het recht om een klacht in te dienen bij de bevoegde toezichthoudende autoriteit voor gegevensbescherming als je van mening bent dat je privacyrechten zijn geschonden.

## 13. Contact

Voor vragen over dit Privacybeleid:

- E-mail: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)
